import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';

import videoInputs from '../video-input/video-input-store';
import {
  GetAllVideoEncodersResponse,
  ResolutionsMatrix,
  VideoEncoder,
  VideoEncoderResponse,
  VideoEncoderViewModel,
  mapVideoEncoderViewModel,
  mapVideoEncodersResponse,
} from './video-encoder-models';

// values will be inited from API at runtime
const videoEncoders: VideoEncoderViewModel[] = [];
createWatchStore(videoEncoders);
export default videoEncoders;

export const resolutions: ResolutionsMatrix = {};
createWatchStore(resolutions as any);

export const saveResponseToVideoEncoders = (response: VideoEncoderViewModel[]) => {
  updateWatchStore(videoEncoders, response);
};

export const fetchVideoEncoders = (t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllVideoEncodersResponse>(APIPath.videnc.list).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        const viewModels = mapVideoEncodersResponse(response, t);
        saveResponseToVideoEncoders(viewModels);
      }
    }
    return res;
  });
};

export const fetchResolutions = () => {
  if (Object.keys(resolutions).length > 0) {
    return null; // load only once, it cannot change
  }

  return apiClientWithoutHandler.genericController.get<any>(APIPath.videnc.resolutions).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        updateWatchStore(resolutions, response.resolutions);
      }
    }
    return res;
  });
};

export const getAssociatedVideoInput = (videnc?: VideoEncoder | VideoEncoderViewModel) => {
  if (videnc?.interface >= 0) {
    const videoInput = videoInputs[videnc.interface];
    if (videoInput != null) {
      return videoInput;
    }
  }
  return undefined;
};

const replaceViewModel = (response: VideoEncoderResponse, t: TFunction, selected = false) => {
  const viewModel = mapVideoEncoderViewModel(response, t);
  const encoders = [...videoEncoders];
  const index = encoders.findIndex((e) => e.id === viewModel.id);
  if (index > -1) {
    encoders.splice(index, 1, { ...viewModel, selected: selected });
  }
  saveResponseToVideoEncoders(encoders);
};

export const fetchVideoEncoder = (id: number, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.videnc.get, {
    id: id,
  });
  return apiClientWithoutHandler.genericController.get<VideoEncoderResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        replaceViewModel(response, t);
      }
    }
    return res;
  });
};

export const updateVideoEncoder = (model: VideoEncoderViewModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.videnc.update, { id: model.id });
  return apiClientWithoutHandler.genericController
    .put<VideoEncoderResponse>(path, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        t('videnc.notifications.settingsChangeSuccessMsg'),
        t('videnc.notifications.settingsChangeErrorMsg'),
        t,
      );
      return res;
    });
};

export const startStopVideoEncoder = (
  model: VideoEncoderViewModel,
  start: boolean,
  selected: boolean,

  t: TFunction,
) => {
  const path = APIPathWithArguments(start ? APIPath.videnc.start : APIPath.videnc.stop, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<VideoEncoderResponse>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t, selected);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, null, null, t);
      return res;
    });
};

export const fetchVideoEncoderStatistics = (
  model: VideoEncoderViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<VideoEncoderViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.videnc.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.get<VideoEncoderResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapVideoEncoderViewModel(response, t));
      }
    }
    return res;
  });
};

export const resetVideoEncoderStatistics = (
  model: VideoEncoderViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<VideoEncoderViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.videnc.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.delete<VideoEncoderResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapVideoEncoderViewModel(response, t));
      }
    }
    return res;
  });
};
